import React, { useContext, useEffect, useState } from 'react';
import {
	Box,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	Grid,
	Link,
	Typography,
	useTheme,
} from '@mui/material';
import MainButton from '../../UI/MainButton';
import isEmail from 'validator/lib/isEmail';
import SnackbarContext from '../../../store/snackbar-context';
import authContext from '../../../store/auth-context';
import FormTextField from '../../UI/FormTextField';
import FormDropdown from '../../UI/FormDropdown';
import {
	getAllTeachers,
	getDistricts,
	getSchools,
	getSpecializations,
	getTeachersByDistrictId,
} from '../../../api/user-api';
import FormComboBox from '../../UI/FormComboBox';
import { useNavigate } from 'react-router-dom';
import DividerWithButton from '../../UI/DividerWithButton';
import { loginPage, accountPage } from '../../../store/fixed-routing';
import {
	registerNewStudent,
	checkDuplicateName,
	checkPriorStudent,
	verifyPriorStudent,
	sendResetLinkRequest,
	DuplicateNameWarning,
} from '../../../api/auth-api';
import TextWithDivider from '../../UI/TextWithDivider';
import { debugError } from '../../../utils/debug';
import { formatPersonName } from '../../../utils/person-name-case';
import { applyStudentPrefill, StudentPrefill } from '../../../utils/student-prefill';
import school from '../../../model/School';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { isSmallOlympicVariant, getVariantCapability } from '../../../config/variant-profile';
import {
	computeStudentRegisterValidation,
	emptyStudentRegisterValidation,
	studentRegisterErrorMessages,
} from '../../../utils/student-register-validation';

const formErrors = studentRegisterErrorMessages;

const RegisterStudent = ({
	prefill,
	resumeMode = false,
}: {
	prefill?: StudentPrefill | null;
	resumeMode?: boolean;
}) => {
	const navigate = useNavigate();
	const { token, establishSession, email: loggedInEmail } = useContext(authContext);
	const isSmallOlympic = isSmallOlympicVariant();
	const specializationEnabled = getVariantCapability('registration.specializationEnabled');
	const studentPhoneRequired = getVariantCapability('registration.studentPhoneRequired');
	const guardianRolesSplit = getVariantCapability('registration.guardianRolesSplit');
	const maxScientificGuardians = getVariantCapability('registration.maxScientificGuardians');
	const maxSchoolGuardians = getVariantCapability('registration.maxSchoolGuardians');
	const { palette, typography } = useTheme();
	const [districts, setDistricts] = useState<any>();
	const [schools, setSchools] = useState<any>();
	const [specializations, setSpecializations] = useState<any>();
	const [allTeachers, setAllTeachers] = useState<any>();
	const [districtTeachers, setDistrictTeachers] = useState<any>();
	const [showAllTeachers, setShowAllTeachers] = useState<boolean>(false);
	const [districtsDisabled, setDistrictsDisabled] = useState<boolean>(true);
	const [validated, setValidated] = useState(false);
	const [nameWarnings, setNameWarnings] = useState<DuplicateNameWarning[]>([]);
	const { setMsg } = useContext(SnackbarContext);
	const [formInfo, setFormInfo] = useState({
		personalTitle: '',
		parentName: '',
		parentEmail: '',
		firstName: '',
		secondName: '',
		lastName: '',
		class: '',
		specializationId: '',
		email: '',
		phone: '',
		password: '',
		passwordConfirm: '',
		districtId: '',
		teachersIds: [] as number[],
		scientificTeacherIds: [] as number[],
		schoolTeacherId: null as number | null,
		street: '',
		building: '',
		apartment: '',
		zipCode: '',
		city: '',
		schoolId: '',
		specialCertificate: false,
		// Mała olimpiada (wakacje 2026 #8): tylko ścieżka niepełnoletniego, bez przełącznika w UI.
		isStudentUnderage: isSmallOlympic,
		/** Ukryte — przenoszone z poprzedniej edycji (brak pola w formularzu). */
		birthday: '',
	});

	const [helperInputInfo, setHelperInputInfo] = useState({
		district: '',
		teachers: [] as string[],
		scientificTeachers: [] as string[],
		schoolTeacher: '' as string,
		specialization: '',
		school: '',
		rodo: '',
		consent: '',
	});

	const maximumNumberOfTeachers = getVariantCapability('registration.maxTeachersForStudent');

	const [districtNames, setDistrictNames] = useState<any>([]);
	const [specializationNames, setSpecializationNames] = useState<any>([]);
	const [schoolNames, setSchoolNames] = useState<any>([]);
	const [allTeachersNames, setAllTeachersNames] = useState<any>([]);
	const [districtTeachersNames, setDistrictTeachersNames] = useState<any>([]);
	const [isSecondName, setIsSecondName] = useState(false);
	const [opinionFiles, setOpinionFiles] = useState<File[]>([]);
	const [skipPassword, setSkipPassword] = useState(false);
	const [priorMatchHint, setPriorMatchHint] = useState<string | null>(null);
	const [priorRequiresPassword, setPriorRequiresPassword] = useState(false);
	const [priorVerified, setPriorVerified] = useState(false);
	const [priorPasswordDialogOpen, setPriorPasswordDialogOpen] = useState(false);
	const [priorVerifyPassword, setPriorVerifyPassword] = useState('');
	const [priorVerifyLoading, setPriorVerifyLoading] = useState(false);
	const [priorResetLoading, setPriorResetLoading] = useState(false);
	const [priorResetSent, setPriorResetSent] = useState(false);
	const [formValidation, setFormValidation] = useState(emptyStudentRegisterValidation);
	/** Prefill z check/verify-prior, gdy katalogi (okręgi/szkoły) jeszcze się ładują. */
	const [pendingPriorPrefill, setPendingPriorPrefill] = useState<{
		prefill: StudentPrefill;
		password?: string;
		skipPassword?: boolean;
	} | null>(null);

	const syncValidationAfterPrefill = (
		applied: ReturnType<typeof applyStudentPrefill>,
		formOverrides: Partial<typeof formInfo> = {},
		helperOverrides: Partial<typeof helperInputInfo> = {},
		options: { skipPassword?: boolean } = {},
	) => {
		const mergedForm = { ...formInfo, ...applied.appliedForm, ...formOverrides };
		const mergedHelper = {
			...helperInputInfo,
			district: applied.appliedHelperDistrict || helperInputInfo.district,
			school: applied.appliedHelperSchool || helperInputInfo.school,
			specialization: applied.appliedHelperSpecialization || helperInputInfo.specialization,
			...helperOverrides,
		};
		setFormValidation(
			computeStudentRegisterValidation({
				formInfo: mergedForm,
				helperInputInfo: mergedHelper,
				isSecondName: applied.hasSecondName,
				isSmallOlympic,
				skipPassword: options.skipPassword ?? skipPassword,
				guardianRolesSplit,
				specializationEnabled,
				studentPhoneRequired,
			}),
		);
		setValidated(false);
	};

	const applyPriorPrefillPayload = (
		prefillData: StudentPrefill,
		options: { password?: string; skipPassword?: boolean } = {},
	) => {
		if (!districts) {
			setPendingPriorPrefill({
				prefill: prefillData,
				password: options.password,
				skipPassword: options.skipPassword,
			});
			return;
		}
		const applied = applyStudentPrefill(
			prefillData,
			districts,
			schools,
			specializations,
			setFormInfo,
			setHelperInputInfo,
			setIsSecondName,
			isSmallOlympic,
		);
		setDistrictsDisabled(applied.schoolMatched);
		const formOverrides = options.password ? { password: options.password, passwordConfirm: options.password } : {};
		syncValidationAfterPrefill(applied, formOverrides, {}, { skipPassword: options.skipPassword });
		setPendingPriorPrefill(null);
	};

	useEffect(() => {
		const fetchData = async () => {
			const districts = await getDistricts();
			const schools = await getSchools();
			const specializations = await getSpecializations();
			const allTeachers = await getAllTeachers();
			setDistricts(districts);
			setSchools(schools);
			setSpecializations(specializations);
			setAllTeachers(allTeachers);

			if (districts) {
				setDistrictNames(districts.map((district: any) => district.name));
			}

			if (specializations) {
				setSpecializationNames(specializations.map((specialization: any) => specialization.name));
				if (!specializationEnabled) {
					const bez = specializations.find(
						(s: any) =>
							String(s.name || '')
								.trim()
								.toLowerCase() === 'bez specjalizacji',
					);
					if (bez) {
						setFormInfo((prev: any) => ({ ...prev, specializationId: bez.id }));
						setHelperInputInfo((prev: any) => ({ ...prev, specialization: bez.name }));
						setFormValidation((prev: any) => ({ ...prev, specializationError: '' }));
					}
				}
			}

			if (schools) {
				const schoolList = schools
					.sort((a: any, b: any) => {
						return a.city.localeCompare(b.city);
					})
					.map((school: any) => school.city.toUpperCase() + ', ' + school.name.toUpperCase());
				setSchoolNames(Array.from(new Set(schoolList)));
			}

			if (allTeachers) {
				const teachersList = allTeachers.map(
					(teacher: any) => teacher.degree + ' ' + teacher.name + ' ' + teacher.surname,
				);
				setAllTeachersNames(Array.from(new Set(teachersList)));
			}
		};
		fetchData().catch((error) => debugError('RegisterStudent fetchData error:', error));
	}, []);

	useEffect(() => {
		if (!prefill || !districts) {
			return;
		}
		const applied = applyStudentPrefill(
			prefill,
			districts,
			schools,
			specializations,
			setFormInfo,
			setHelperInputInfo,
			setIsSecondName,
			isSmallOlympic,
		);
		setDistrictsDisabled(applied.schoolMatched);
		const emailOverride = resumeMode && loggedInEmail ? { email: loggedInEmail } : {};
		syncValidationAfterPrefill(applied, emailOverride, {}, { skipPassword: resumeMode || skipPassword });
		if (resumeMode && loggedInEmail) {
			setFormInfo((prev) => ({ ...prev, email: loggedInEmail }));
		}
		if (resumeMode) {
			setSkipPassword(true);
			setPriorVerified(true);
			setPriorMatchHint(
				'Dane z poprzedniej edycji zostały wczytane — wybierz nauczycieli i zaznacz zgody na bieżącą edycję.',
			);
		}
	}, [prefill, districts, schools, specializations, resumeMode, loggedInEmail, isSmallOlympic]);

	useEffect(() => {
		if (!pendingPriorPrefill || !districts) {
			return;
		}
		applyPriorPrefillPayload(pendingPriorPrefill.prefill, {
			password: pendingPriorPrefill.password,
			skipPassword: pendingPriorPrefill.skipPassword,
		});
	}, [pendingPriorPrefill, districts, schools, specializations]);

	useEffect(() => {
		const fetchData = async () => {
			setDistrictTeachersNames([]);
			const districtTeachers = await getTeachersByDistrictId(`${formInfo.districtId}`);
			setDistrictTeachers(districtTeachers);

			if (districtTeachers) {
				const teachersList = districtTeachers.map(
					(teacher: any) => teacher.degree + ' ' + teacher.name + ' ' + teacher.surname,
				);
				setDistrictTeachersNames(teachersList);
			}
		};
		fetchData().catch((error) => debugError('RegisterStudent fetchData error:', error));
	}, [formInfo.districtId, helperInputInfo.school, helperInputInfo.district]);

	const refreshNameWarnings = async (names?: { firstName?: string; lastName?: string }) => {
		const firstName = formatPersonName(names?.firstName ?? formInfo.firstName);
		const lastName = formatPersonName(names?.lastName ?? formInfo.lastName);
		if (!firstName || !lastName || !formInfo.districtId) {
			setNameWarnings([]);
			return;
		}
		try {
			const result = await checkDuplicateName({
				firstName,
				lastName,
				districtId: formInfo.districtId,
				excludeEmail: formInfo.email || undefined,
				role: 'student',
			});
			setNameWarnings(result?.warnings ?? []);
		} catch (error) {
			debugError('checkDuplicateName failed', error);
		}
	};

	const normalizeStudentNameFields = () => {
		setFormInfo((prev) => ({
			...prev,
			firstName: formatPersonName(prev.firstName),
			secondName: isSecondName ? formatPersonName(prev.secondName) : prev.secondName,
			lastName: formatPersonName(prev.lastName),
		}));
	};

	const firstNameBlur = () => {
		const firstName = formatPersonName(formInfo.firstName);
		const lastName = formatPersonName(formInfo.lastName);
		if (firstName !== formInfo.firstName) {
			setFormInfo((prev) => ({ ...prev, firstName }));
		}
		void refreshNameWarnings({ firstName, lastName });
	};

	const lastNameBlur = () => {
		const firstName = formatPersonName(formInfo.firstName);
		const lastName = formatPersonName(formInfo.lastName);
		if (lastName !== formInfo.lastName) {
			setFormInfo((prev) => ({ ...prev, lastName }));
		}
		void refreshNameWarnings({ firstName, lastName });
	};

	const secondNameBlur = () => {
		const secondName = formatPersonName(formInfo.secondName);
		if (secondName !== formInfo.secondName) {
			setFormInfo((prev) => ({ ...prev, secondName }));
		}
	};

	const titleHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				titleError: formErrors.titleError,
			});
		} else {
			setFormValidation({
				...formValidation,
				titleError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				personalTitle: value,
			};
		});
	};

	const nameHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				nameError: formErrors.nameError,
			});
		} else {
			setFormValidation({
				...formValidation,
				nameError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				firstName: value,
			};
		});
	};

	const parentNameHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				parentNameError: formErrors.parentNameError,
			});
		} else {
			setFormValidation({
				...formValidation,
				parentNameError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				parentName: value,
			};
		});
	};

	const secondNameHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				secondNameError: formErrors.secondNameError,
			});
		} else {
			setFormValidation({
				...formValidation,
				secondNameError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				secondName: value,
			};
		});
	};

	const surnameHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				surnameError: formErrors.surnameError,
			});
		} else {
			setFormValidation({
				...formValidation,
				surnameError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				lastName: value,
			};
		});
	};

	const classHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				classError: formErrors.classError,
			});
		} else {
			setFormValidation({
				...formValidation,
				classError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				class: value,
			};
		});
	};

	const specializationHandler = (value: string) => {
		const specialization = specializations.find((specialization: any) => specialization.name === value);
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				specializationError: formErrors.specializationError,
			});
		} else {
			setFormValidation({
				...formValidation,
				specializationError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				specializationId: specialization && specialization.id,
			};
		});
		setHelperInputInfo((prevState) => {
			return {
				...prevState,
				specialization: value,
			};
		});
	};

	const emailHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				emailError: formErrors.emailError,
			});
		} else if (!isEmail(value)) {
			setFormValidation({
				...formValidation,
				emailError: 'Niepoprawny adres e-mail',
			});
		} else {
			setFormValidation({
				...formValidation,
				emailError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				email: value,
				// Wakacje 2026 #9: jeden adres email kontaktowy — przy niepełnoletnim
				// to samo pole zasila też rekord rodzica/opiekuna w bazie.
				...(prevState.isStudentUnderage ? { parentEmail: value } : {}),
			};
		});
	};

	const checkEmailForPriorProfile = async () => {
		if (!formInfo.email || !formInfo.firstName || !formInfo.lastName) {
			return;
		}
		if (!isEmail(formInfo.email)) {
			return;
		}
		try {
			const result = await checkPriorStudent(
				formInfo.email,
				formInfo.firstName,
				formInfo.lastName,
				formInfo.districtId || undefined,
			);
			if (result?.match) {
				if (result.requiresPassword) {
					setPriorMatchHint(
						'Rozpoznaliśmy konto z poprzedniej edycji — potwierdź hasło, aby wczytać dane kontaktowe na bieżącą edycję.',
					);
					setPriorRequiresPassword(true);
					setPriorVerified(false);
					setSkipPassword(false);
					setPriorVerifyPassword('');
					setPriorPasswordDialogOpen(true);
				} else {
					setPriorMatchHint(
						'Rozpoznaliśmy profil z poprzedniej edycji — uzupełnij hasło, nauczycieli i zgody na bieżącą edycję.',
					);
					setPriorRequiresPassword(false);
					setPriorVerified(true);
					// Brak konta WP — trzeba ustawić hasło przy rejestracji.
					setSkipPassword(false);
					if (result.prefill) {
						applyPriorPrefillPayload(result.prefill);
					}
				}
				setNameWarnings(result.nameWarnings ?? []);
			} else {
				setPriorMatchHint(null);
				setPriorRequiresPassword(false);
				setPriorVerified(false);
				setSkipPassword(false);
				setPriorPasswordDialogOpen(false);
			}
		} catch (error) {
			debugError('checkPriorStudent failed', error);
		}
	};

	const requestPriorPasswordReset = async () => {
		if (!formInfo.email) {
			setMsg({ msg: 'Podaj adres e-mail w formularzu.', severity: 'warning' });
			return;
		}
		setPriorResetLoading(true);
		try {
			const result = await sendResetLinkRequest(formInfo.email, (error) => {
				setMsg({ msg: error, severity: 'error' });
			});
			if (result?.user_exists === false) {
				setMsg({ msg: result.message || 'Nie znaleziono konta dla tego adresu e-mail.', severity: 'warning' });
				return;
			}
			setPriorResetSent(true);
			setMsg({
				msg: 'Wysłaliśmy link do resetu hasła. Ustaw nowe hasło z wiadomości, wróć tutaj i wpisz je w oknie poniżej.',
				severity: 'success',
			});
		} catch (error) {
			debugError('requestPriorPasswordReset failed', error);
		} finally {
			setPriorResetLoading(false);
		}
	};

	const confirmPriorPassword = async () => {
		if (!formInfo.email || !priorVerifyPassword) {
			setMsg({ msg: 'Podaj hasło do istniejącego konta.', severity: 'warning' });
			return;
		}
		setPriorVerifyLoading(true);
		try {
			const result = await verifyPriorStudent(
				formInfo.email,
				priorVerifyPassword,
				formInfo.firstName,
				formInfo.lastName,
			);
			if (!result?.ok) {
				setMsg({ msg: result?.error || 'Nieprawidłowe hasło.', severity: 'error' });
				return;
			}
			setPriorVerified(true);
			setPriorRequiresPassword(false);
			setSkipPassword(true);
			setPriorPasswordDialogOpen(false);
			setPriorMatchHint(
				'Dane z poprzedniej edycji zostały wczytane — wybierz nauczycieli i zaznacz zgody na bieżącą edycję.',
			);
			if (result.prefill) {
				applyPriorPrefillPayload(result.prefill, {
					password: priorVerifyPassword,
					skipPassword: true,
				});
			}
			setFormInfo((prev) => ({
				...prev,
				password: priorVerifyPassword,
				passwordConfirm: priorVerifyPassword,
			}));
		} finally {
			setPriorVerifyLoading(false);
		}
	};

	const phoneHandler = (value: string) => {
		setFormInfo((prevState) => {
			return {
				...prevState,
				phone: value,
			};
		});
	};

	const passwordHandler = (value: string) => {
		if (value.length > 0 && value.length < 8) {
			setFormValidation({
				...formValidation,
				passwordError: formErrors.passwordError,
			});
		} else {
			setFormValidation({
				...formValidation,
				passwordError: '',
			});
		}
		setFormInfo((prevState) => ({
			...prevState,
			password: value,
		}));
	};

	const passwordConfirmHandler = (value: string) => {
		if (value.length > 0 && value !== formInfo.password) {
			setFormValidation({
				...formValidation,
				passwordConfirmError: formErrors.passwordConfirmError,
			});
		} else {
			setFormValidation({
				...formValidation,
				passwordConfirmError: '',
			});
		}
		setFormInfo((prevState) => ({
			...prevState,
			passwordConfirm: value,
		}));
	};

	const schoolHandler = (e: any, value: string) => {
		setDistrictsDisabled(false);

		const school = schools.find((school: any) => {
			return school.city.toUpperCase() + ', ' + school.name.toUpperCase() === value.toUpperCase();
		});
		const district = districts.find((district: any) => school && district.id === school.districtID);
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				schoolError: formErrors.schoolError,
			});
		} else if (district && school) {
			setFormValidation({
				...formValidation,
				schoolError: '',
				districtError: '',
			});
		} else {
			setFormValidation({
				...formValidation,
				schoolError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				schoolId: school && school.id,
				districtId: district && district.id,
			};
		});
		setHelperInputInfo((prevState) => {
			return {
				...prevState,
				school: value.toUpperCase(),
				district: district && district.name,
			};
		});
		if (district?.id && formInfo.firstName && formInfo.lastName) {
			checkDuplicateName({
				firstName: formInfo.firstName,
				lastName: formInfo.lastName,
				districtId: district.id,
				excludeEmail: formInfo.email || undefined,
				role: 'student',
			})
				.then((result) => setNameWarnings(result?.warnings ?? []))
				.catch((error) => debugError('checkDuplicateName failed', error));
		}
	};

	const districtHandler = (value: string) => {
		const district = districts.find((district: any) => district.name === value);
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				districtError: formErrors.districtError,
			});
		} else {
			setFormValidation({
				...formValidation,
				districtError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				districtId: district.id,
			};
		});
		setHelperInputInfo((prevState) => {
			return {
				...prevState,
				district: value,
			};
		});
		if (district?.id && formInfo.firstName && formInfo.lastName) {
			checkDuplicateName({
				firstName: formInfo.firstName,
				lastName: formInfo.lastName,
				districtId: district.id,
				excludeEmail: formInfo.email || undefined,
				role: 'student',
			})
				.then((result) => setNameWarnings(result?.warnings ?? []))
				.catch((error) => debugError('checkDuplicateName failed', error));
		}
	};

	const teacherHandler = (e: any, values: any) => {
		const teachersIds: any = [];
		values.forEach((value: any) => {
			const teacher = allTeachers.find(
				(teacher: any) => teacher.degree + ' ' + teacher.name + ' ' + teacher.surname === value,
			);
			if (teacher) {
				teachersIds.push(teacher.id);
			}
		});
		if (values.length < 1) {
			setFormValidation({
				...formValidation,
				teacherError: formErrors.teacherError,
			});
		} else {
			setFormValidation({
				...formValidation,
				teacherError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				teachersIds: teachersIds,
			};
		});
		setHelperInputInfo((prevState) => {
			return {
				...prevState,
				teachers: values,
			};
		});
	};

	const resolveTeacherIdsFromLabels = (values: string[]) => {
		const ids: number[] = [];
		values.forEach((value) => {
			const teacher = allTeachers.find((t: any) => t.degree + ' ' + t.name + ' ' + t.surname === value);
			if (teacher) {
				ids.push(teacher.id);
			}
		});
		return ids;
	};

	const scientificTeachersHandler = (_e: any, values: string[]) => {
		const ids = resolveTeacherIdsFromLabels(values);
		setFormValidation((prev) => ({
			...prev,
			teacherError: '',
		}));
		setFormInfo((prev) => ({
			...prev,
			scientificTeacherIds: ids,
			teachersIds: [...ids, ...(prev.schoolTeacherId ? [prev.schoolTeacherId] : [])].filter(
				(id, i, arr) => arr.indexOf(id) === i,
			),
		}));
		setHelperInputInfo((prev) => ({ ...prev, scientificTeachers: values }));
	};

	const schoolTeacherHandler = (_e: any, value: string) => {
		const ids = value ? resolveTeacherIdsFromLabels([value]) : [];
		const schoolId = ids[0] ?? null;
		setFormInfo((prev) => ({
			...prev,
			schoolTeacherId: schoolId,
			teachersIds: [...(prev.scientificTeacherIds || []), ...(schoolId ? [schoolId] : [])].filter(
				(id, i, arr) => arr.indexOf(id) === i,
			),
		}));
		setHelperInputInfo((prev) => ({ ...prev, schoolTeacher: value || '' }));
	};

	/** Filo: naukowy / szkolno-naukowy → lista naukowych; szkolny / szkolno-naukowy → lista szkolnego (tylko okręg). */
	const teacherLabel = (t: any) => `${t.degree} ${t.name} ${t.surname}`;
	const scientificPoolNames = (showAllTeachers ? allTeachers : districtTeachers || [])
		.filter((t: any) => {
			const cat = String(t.category || '');
			return cat.includes('naukowy') || cat.includes('szkolno-naukowy') || t.role_is_scientific_supervisor;
		})
		.map(teacherLabel);
	const schoolPoolNames = (districtTeachers || [])
		.filter((t: any) => {
			const cat = String(t.category || '');
			const isScientificOnly = (cat === 'Opiekun naukowy' || cat.includes('naukowy')) && !cat.includes('szkolno');
			return !isScientificOnly;
		})
		.map(teacherLabel);

	const streetHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				streetError: formErrors.streetError,
			});
		} else {
			setFormValidation({
				...formValidation,
				streetError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				street: value,
			};
		});
	};

	const buildingHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				buildingError: formErrors.buildingError,
			});
		} else {
			setFormValidation({
				...formValidation,
				buildingError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				building: value,
			};
		});
	};

	const apartmentHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				apartmentError: formErrors.apartmentError,
			});
		} else {
			setFormValidation({
				...formValidation,
				apartmentError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				apartment: value,
			};
		});
	};

	const zipCodeHandler = (value: string) => {
		const numericValue = value.replace(/\D/g, '');
		const formattedValue = numericValue.replace(/(\d{2})(\d{1,})/, '$1-$2');
		formInfo.zipCode = formattedValue;

		if (formattedValue.length === 6) {
			setFormValidation({
				...formValidation,
				zipCodeError: '',
			});
		} else {
			setFormValidation({
				...formValidation,
				zipCodeError: formErrors.zipCodeError,
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				zipCode: formattedValue,
			};
		});
	};

	const cityHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				cityError: formErrors.cityError,
			});
		} else {
			setFormValidation({
				...formValidation,
				cityError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				city: value,
			};
		});
	};

	const rodoHandler = (e: any) => {
		if (!e.target.checked) {
			setFormValidation({
				...formValidation,
				rodoError: formErrors.rodoError,
			});
		} else {
			setFormValidation({
				...formValidation,
				rodoError: '',
			});
		}
		setHelperInputInfo((prevState) => {
			return {
				...prevState,
				rodo: e.target.checked,
			};
		});
	};

	const consentHandler = (e: any) => {
		if (!e.target.checked) {
			setFormValidation({
				...formValidation,
				consentError: formErrors.consentError,
			});
		} else {
			setFormValidation({
				...formValidation,
				consentError: '',
			});
		}
		setHelperInputInfo((prevState) => {
			return {
				...prevState,
				consent: e.target.checked,
			};
		});
	};

	const specialCertificateHandler = (e: any) => {
		const checked = e.target.checked;
		setFormInfo((prevState) => {
			return {
				...prevState,
				specialCertificate: checked,
			};
		});
		if (!checked) {
			setOpinionFiles([]);
		}
	};

	const adultStudentHandler = (e: any) => {
		setFormInfo((prevState) => {
			return {
				...prevState,
				isStudentUnderage: e.target.checked,
			};
		});
	};

	const resetInputs = () => {
		setFormInfo((prevState) => {
			return {
				...prevState,
				personalTitle: '',
				parentName: '',
				parentEmail: '',
				firstName: '',
				secondName: '',
				lastName: '',
				class: '',
				specializationId: '',
				email: '',
				phone: '',
				password: '',
				passwordConfirm: '',
				districtId: '',
				teachersIds: [] as number[],
				scientificTeacherIds: [] as number[],
				schoolTeacherId: null as number | null,
				street: '',
				building: '',
				apartment: '',
				zipCode: '',
				city: '',
				schoolId: '',
				specialCertificate: false,
				isStudentUnderage: isSmallOlympic,
				birthday: '',
			};
		});
		setOpinionFiles([]);
		setHelperInputInfo((prevState) => {
			return {
				...prevState,
				school: '',
				district: '',
				specialization: '',
				teachers: [] as string[],
				scientificTeachers: [] as string[],
				schoolTeacher: '',
				rodo: '',
				consent: '',
			};
		});
		setValidated(false);
	};

	const [loading, setLoading] = useState(false);
	const [summaryOpen, setSummaryOpen] = useState(false);

	const getPoleWord = (count: number) => {
		if (count === 1) return 'pole';
		const mod10 = count % 10;
		const mod100 = count % 100;
		if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
			return 'pola';
		}
		return 'pól';
	};

	const buildSummarySections = () => {
		const row = (label: string, value?: string | null) =>
			value && value.trim() !== '' ? { label, value: value.trim() } : null;
		const studentRows = [
			row('Tytuł', formInfo.personalTitle),
			row('Imię', formInfo.firstName),
			row('Drugie imię', formInfo.secondName),
			row('Nazwisko', formInfo.lastName),
			row('Email', formInfo.email),
			row('Telefon', formInfo.phone),
			row('Adres', `${formInfo.street} ${formInfo.building}${formInfo.apartment ? `/${formInfo.apartment}` : ''}`),
			row('Kod pocztowy', formInfo.zipCode),
			row('Miasto', formInfo.city),
		].filter(Boolean) as { label: string; value: string }[];
		const schoolRows = [
			row('Klasa', String(formInfo.class)),
			...(specializationEnabled ? [row('Specjalizacja', helperInputInfo.specialization)] : []),
			row('Szkoła', helperInputInfo.school),
			row('Okręg', helperInputInfo.district),
		].filter(Boolean) as { label: string; value: string }[];
		const teacherRows = Array.isArray(helperInputInfo.teachers)
			? helperInputInfo.teachers
					.filter((t: string) => !!t && t.trim() !== '')
					.map((t: string, idx: number) => ({ label: `Nauczyciel ${idx + 1}`, value: t }))
			: [];
		const parentRows = formInfo.isStudentUnderage
			? [row('Imię i nazwisko rodzica/opiekuna', formInfo.parentName)].filter(Boolean)
			: [];
		const extraRows = [
			formInfo.specialCertificate
				? {
						label: 'Orzeczenie/opinia o dysgrafii',
						value:
							opinionFiles.length > 0
								? `Tak — pliki: ${opinionFiles.map((f) => f.name).join(', ')}`
								: 'Tak (bez pliku — można wgrać na Koncie)',
					}
				: null,
			helperInputInfo.rodo ? { label: 'Zgoda RODO', value: 'Tak' } : null,
			helperInputInfo.consent ? { label: 'Zgodność danych ze stanem faktycznym', value: 'Tak' } : null,
		].filter(Boolean) as { label: string; value: string }[];
		return [
			{ title: 'Dane ucznia', rows: studentRows },
			{ title: 'Dane szkolne', rows: schoolRows },
			{ title: 'Dane nauczycieli', rows: teacherRows },
			{ title: 'Dane rodzica/opiekuna', rows: parentRows as { label: string; value: string }[] },
			{ title: 'Dodatkowe', rows: extraRows },
		].filter((section) => section.rows.length > 0);
	};

	const submitRegistration = () => {
		if (loading) {
			return;
		}
		setLoading(true);
		const payload = {
			...formInfo,
			firstName: formatPersonName(formInfo.firstName),
			secondName: isSecondName ? formatPersonName(formInfo.secondName) : formInfo.secondName,
			lastName: formatPersonName(formInfo.lastName),
			// Wakacje 2026 #9: jeden email kontaktowy — przy niepełnoletnim zapisujemy go też jako parent_email.
			parentEmail: formInfo.isStudentUnderage ? formInfo.email : formInfo.parentEmail,
			password: skipPassword ? formInfo.password || priorVerifyPassword : formInfo.password,
			passwordConfirm: skipPassword ? formInfo.passwordConfirm || priorVerifyPassword : formInfo.passwordConfirm,
			birthday: formInfo.birthday || '',
		};
		registerNewStudent(payload, (error) => error, opinionFiles)
			.then(async (res: any) => {
				if (typeof res === 'string' && res !== '') {
					setMsg({ msg: res, severity: 'error' });
					return;
				}
				if (res?.error) {
					setMsg({ msg: res.error, severity: 'error' });
					return;
				}
				if (res?.message) {
					setMsg({ msg: res.message, severity: 'success' });
					setSummaryOpen(false);
					if (res.needsEmailConfirmation) {
						resetInputs();
						navigate('/');
						return;
					}
					if (res.resume) {
						const email = formInfo.email;
						const resumePassword = formInfo.password;
						if (token) {
							resetInputs();
							navigate(accountPage);
							return;
						}
						if (resumePassword && email) {
							const loggedIn = await establishSession(email, resumePassword);
							resetInputs();
							navigate(loggedIn ? accountPage : loginPage);
							return;
						}
						resetInputs();
						navigate(loginPage);
						return;
					}
					resetInputs();
				}
			})
			.catch(() => {
				setMsg({
					msg: 'Nie udało się zarejestrować konta. Prosimy spróbować ponownie później.',
					severity: 'error',
				});
			})
			.finally(() => setLoading(false));
	};

	const actionSend = () => {
		if (priorRequiresPassword && !priorVerified) {
			setPriorPasswordDialogOpen(true);
			setMsg({ msg: 'Potwierdź hasło do konta z poprzedniej edycji.', severity: 'warning' });
			return;
		}
		const validation = computeStudentRegisterValidation({
			formInfo,
			helperInputInfo,
			isSecondName,
			isSmallOlympic,
			skipPassword,
			guardianRolesSplit,
			specializationEnabled,
			studentPhoneRequired,
		});
		setFormValidation(validation);
		setValidated(true);

		if (validation.titleError !== '') {
			setMsg({ msg: validation.titleError, severity: 'warning' });
			return;
		}
		if (validation.nameError !== '') {
			setMsg({ msg: validation.nameError, severity: 'warning' });
			return;
		}
		if (formInfo.isStudentUnderage && validation.parentNameError !== '') {
			setMsg({ msg: validation.parentNameError, severity: 'warning' });
			return;
		}
		if (isSecondName && validation.secondNameError !== '') {
			setMsg({ msg: validation.secondNameError, severity: 'warning' });
			return;
		}
		if (validation.surnameError !== '') {
			setMsg({ msg: validation.surnameError, severity: 'warning' });
			return;
		}
		if (validation.streetError !== '') {
			setMsg({ msg: validation.streetError, severity: 'warning' });
			return;
		}
		if (validation.buildingError !== '') {
			setMsg({ msg: validation.buildingError, severity: 'warning' });
			return;
		}
		if (validation.apartmentError !== '') {
			setMsg({ msg: validation.apartmentError, severity: 'warning' });
			return;
		}
		if (validation.zipCodeError !== '') {
			setMsg({ msg: validation.zipCodeError, severity: 'warning' });
			return;
		}
		if (validation.cityError !== '') {
			setMsg({ msg: validation.cityError, severity: 'warning' });
			return;
		}
		if (validation.emailError !== '') {
			setMsg({ msg: validation.emailError, severity: 'warning' });
			return;
		}
		if (validation.phoneError !== '') {
			setMsg({ msg: validation.phoneError, severity: 'warning' });
			return;
		}
		if (validation.classError !== '') {
			setMsg({ msg: validation.classError, severity: 'warning' });
			return;
		}
		if (specializationEnabled && validation.specializationError !== '') {
			setMsg({ msg: validation.specializationError, severity: 'warning' });
			return;
		}
		if (validation.schoolError !== '') {
			setMsg({ msg: validation.schoolError, severity: 'warning' });
			return;
		}
		if (validation.districtError !== '') {
			setMsg({ msg: validation.districtError, severity: 'warning' });
			return;
		}
		if (validation.teacherError !== '') {
			setMsg({ msg: validation.teacherError, severity: 'warning' });
			return;
		}
		if (validation.rodoError !== '') {
			setMsg({ msg: validation.rodoError, severity: 'warning' });
			return;
		}
		if (validation.consentError !== '') {
			setMsg({ msg: validation.consentError, severity: 'warning' });
			return;
		}
		if (!skipPassword) {
			if (formInfo.password.length < 8) {
				setMsg({ msg: formErrors.passwordError, severity: 'warning' });
				return;
			}
			if (formInfo.password !== formInfo.passwordConfirm) {
				setMsg({ msg: formErrors.passwordConfirmError, severity: 'warning' });
				return;
			}
		}
		normalizeStudentNameFields();
		setSummaryOpen(true);
	};

	const summarySections = buildSummarySections();
	const sectionIconByTitle: Record<string, JSX.Element> = {
		'Dane ucznia': <PersonOutlineIcon fontSize='small' />,
		'Dane szkolne': <SchoolOutlinedIcon fontSize='small' />,
		'Dane nauczycieli': <Groups2OutlinedIcon fontSize='small' />,
		'Dane rodzica/opiekuna': <Groups2OutlinedIcon fontSize='small' />,
		Dodatkowe: <InfoOutlinedIcon fontSize='small' />,
	};

	return (
		<Grid
			width={'100%'}
			maxWidth={'650px'}
			display={'flex'}
			flexDirection={'column'}
			gap={'24px'}
		>
			<Typography
				variant={'h3'}
				fontWeight={'500'}
				color={palette.primary.main}
			>
				Zarejestruj się - uczeń
			</Typography>
			{resumeMode && (
				<Typography color='text.secondary'>
					Uzupełnij rejestrację na bieżącą edycję. Podpowiadamy dane z poprzedniej edycji — nauczycieli i zgody wybierz
					/ potwierdź na nowo.
				</Typography>
			)}
			<Grid
				component={'form'}
				display={'flex'}
				flexDirection={'column'}
			>
				<TextWithDivider
					text={formInfo.isStudentUnderage ? 'Informacje o rodzicu/opiekunie' : 'Informacje o Tobie'}
					sx={{
						mt: '16px',
						mb: '24px',
					}}
				/>
				<Box
					display={'flex'}
					flexDirection={'column'}
					alignItems={'flex-start'}
					gap={'16px'}
					width={'100%'}
					mb={'16px'}
				>
					{isSmallOlympic ? (
						<Typography
							variant='body2'
							sx={{ color: palette.primary.main }}
						>
							Rejestracja dotyczy uczestnika niepełnoletniego — dane rodzica/opiekuna prawnego podaje się poniżej.
						</Typography>
					) : (
						<Box
							sx={{
								'& span': {
									fontSize: typography.subtitle2.fontSize,
									fontWeight: '500',
									color: palette.primary.main,
								},
							}}
						>
							<FormControlLabel
								control={
									<Checkbox
										sx={{
											borderRadius: 3,
											width: 16,
											height: 16,
											mr: '6px',
										}}
									/>
								}
								label='Uczestnik niepełnoletni'
								onChange={adultStudentHandler}
								sx={{
									ml: '2px',
								}}
							/>
						</Box>
					)}
					{formInfo.isStudentUnderage && (
						<FormTextField
							id={'parentName'}
							type={'text'}
							label={'Imię i nazwisko rodzica/opiekuna prawnego'}
							validated={validated}
							errorMsg={formValidation.parentNameError}
							value={formInfo.parentName}
							valueChangeHandler={parentNameHandler}
							helperText={'Adres e-mail kontaktowy (rodzica/opiekuna) podajemy w polu "E-mail" poniżej.'}
						/>
					)}
					{formInfo.isStudentUnderage && (
						<TextWithDivider
							text={'Informacje o uczestniku'}
							sx={{
								mt: '16px',
								mb: '24px',
							}}
						/>
					)}
					<FormDropdown
						id={'title'}
						label={'Tytuł (Pan/Pani)'}
						validated={validated}
						errorMsg={formValidation.titleError}
						value={formInfo.personalTitle}
						values={['Pan', 'Pani']}
						valueChangeHandler={titleHandler}
					/>
					<FormTextField
						id={'name'}
						type={'text'}
						label={'Imię'}
						validated={validated}
						errorMsg={formValidation.nameError}
						value={formInfo.firstName}
						valueChangeHandler={nameHandler}
						onBlur={firstNameBlur}
					/>
					<Box
						sx={{
							'& span': {
								fontSize: typography.subtitle2.fontSize,
								fontWeight: '500',
								color: palette.primary.main,
							},
						}}
					>
						<FormControlLabel
							control={
								<Checkbox
									checked={isSecondName}
									sx={{
										borderRadius: 3,
										width: 16,
										height: 16,
										mr: '6px',
									}}
								/>
							}
							label='Czy uczestnik posiada drugie imię?'
							onChange={(e: any) => setIsSecondName(e.target.checked)}
							sx={{
								ml: '2px',
							}}
						/>
					</Box>
					{isSecondName && (
						<FormTextField
							id={'secondName'}
							type={'text'}
							label={'Drugie imię'}
							validated={validated}
							errorMsg={formValidation.secondNameError}
							value={formInfo.secondName}
							valueChangeHandler={secondNameHandler}
							onBlur={secondNameBlur}
						/>
					)}
					<FormTextField
						id={'surname'}
						type={'text'}
						label={'Nazwisko'}
						validated={validated}
						errorMsg={formValidation.surnameError}
						value={formInfo.lastName}
						valueChangeHandler={surnameHandler}
						onBlur={lastNameBlur}
					/>
					{nameWarnings.length > 0 && (
						<Typography
							variant='body2'
							color='warning.main'
						>
							Uwaga: w tym okręgu jest już zarejestrowana osoba o tym samym imieniu i nazwisku (
							{nameWarnings.map((w) => `${w.maskedEmail}${w.edition ? `, ed. ${w.edition}` : ''}`).join('; ')}
							). Możesz kontynuować rejestrację.
						</Typography>
					)}
					<TextWithDivider
						text={'Dane dostępu do konta'}
						dividerColor={palette.secondary.main}
						sx={{
							mt: '32px',
							mb: '8px',
						}}
					/>
					<FormTextField
						id={'email'}
						type={'email'}
						label={formInfo.isStudentUnderage ? 'E-mail (kontaktowy, rodzica/opiekuna prawnego)' : 'E-mail'}
						validated={validated}
						errorMsg={formValidation.emailError}
						helperText={
							'Podaj adres skrzynki, do której masz dostęp — wyślemy link potwierdzający poprawność adresu i aktywację konta.'
						}
						value={formInfo.email}
						valueChangeHandler={emailHandler}
						onBlur={checkEmailForPriorProfile}
					/>
					{priorMatchHint && (
						<Typography
							variant='body2'
							color='primary'
						>
							{priorMatchHint}
						</Typography>
					)}
					<FormTextField
						id={'phone'}
						type={'tel'}
						label={studentPhoneRequired ? 'Numer telefonu' : 'Numer telefonu (opcjonalnie)'}
						validated={validated}
						errorMsg={formValidation.phoneError}
						value={formInfo.phone}
						valueChangeHandler={phoneHandler}
					/>
					{!skipPassword && !(priorRequiresPassword && !priorVerified) && (
						<>
							<FormTextField
								id={'password'}
								type={'password'}
								label={'Hasło'}
								validated={validated}
								errorMsg={formValidation.passwordError}
								value={formInfo.password}
								valueChangeHandler={passwordHandler}
							/>
							<FormTextField
								id={'passwordConfirm'}
								type={'password'}
								label={'Powtórz hasło'}
								validated={validated}
								errorMsg={formValidation.passwordConfirmError}
								value={formInfo.passwordConfirm}
								valueChangeHandler={passwordConfirmHandler}
							/>
						</>
					)}
					{skipPassword && (
						<Typography
							variant='body2'
							color='text.secondary'
						>
							Hasło: używane jest hasło z poprzedniej edycji (można je zmienić później po zalogowaniu).
						</Typography>
					)}
					<TextWithDivider
						text={'Adres'}
						dividerColor={palette.secondary.main}
						sx={{
							mt: '32px',
							mb: '8px',
						}}
					/>
					<Box
						display={'flex'}
						flexDirection={'column'}
						width={'100%'}
						gap={'16px'}
					>
						<FormTextField
							id={'street'}
							type={'text'}
							label={'Adres zamieszkania/korespondencyjny'}
							placeholder='Ulica'
							validated={validated}
							errorMsg={formValidation.streetError}
							value={formInfo.street}
							valueChangeHandler={streetHandler}
						/>
						<FormTextField
							id={'building'}
							type={'text'}
							label={''}
							placeholder='Numer budynku'
							validated={validated}
							errorMsg={formValidation.buildingError}
							value={formInfo.building}
							valueChangeHandler={buildingHandler}
						/>
						<FormTextField
							id={'apartment'}
							type={'text'}
							label={''}
							placeholder='Numer mieszkania'
							validated={validated}
							errorMsg={formValidation.apartmentError}
							value={formInfo.apartment}
							valueChangeHandler={apartmentHandler}
						/>
						<Box
							display={'flex'}
							gap={'16px'}
							justifyContent={'space-between'}
						>
							<FormTextField
								id={'zip'}
								type={'text'}
								label={''}
								placeholder='__-___'
								validated={validated}
								errorMsg={formValidation.zipCodeError}
								value={formInfo.zipCode}
								valueChangeHandler={zipCodeHandler}
							/>
							<FormTextField
								id={'city'}
								type={'text'}
								label={''}
								placeholder='Miasto'
								validated={validated}
								errorMsg={formValidation.cityError}
								value={formInfo.city}
								valueChangeHandler={cityHandler}
							/>
						</Box>
					</Box>
					<TextWithDivider
						text={'Informacje o szkole'}
						dividerColor={palette.secondary.main}
						sx={{
							mt: '32px',
							mb: '8px',
						}}
					/>
					<FormDropdown
						id={'class'}
						label={'Klasa'}
						validated={validated}
						errorMsg={formValidation.classError}
						value={formInfo.class}
						values={!isSmallOlympic ? [1, 2, 3, 4, 5] : [4, 5, 6, 7, 8]}
						valueChangeHandler={classHandler}
					/>
					{specializationEnabled && (
						<FormDropdown
							id={'specialization'}
							label={'Specjalizacja'}
							validated={validated}
							errorMsg={formValidation.specializationError}
							value={helperInputInfo.specialization}
							values={specializationNames}
							valueChangeHandler={specializationHandler}
						/>
					)}
					<FormComboBox
						id={'school'}
						label={'Szkoła'}
						validated={validated}
						errorMsg={formValidation.schoolError}
						value={helperInputInfo.school}
						values={schoolNames && schoolNames}
						valueChangeHandler={schoolHandler}
					/>
					<FormDropdown
						id={'district'}
						label={'Okręg'}
						validated={validated}
						errorMsg={formValidation.districtError}
						value={helperInputInfo.district}
						values={districtNames && districtNames}
						valueChangeHandler={districtHandler}
						isDisabled={districtsDisabled}
						disabledLabel='Nazwa okręgu - pojawia się automatycznie po wyborze szkoły'
					/>
					<Box
						display={'flex'}
						flexDirection={'column'}
						width={'100%'}
						gap={'12px'}
						sx={{
							'& span': {
								fontSize: typography.subtitle2.fontSize,
								fontWeight: '500',
								color: palette.primary.main,
							},
						}}
					>
						{guardianRolesSplit ? (
							<>
								<FormComboBox
									id={'scientific-teachers'}
									label={'Opiekunowie naukowi (jeśli już znasz)'}
									multiple={true}
									multipleMax={maxScientificGuardians}
									validated={validated}
									errorMsg={formValidation.teacherError}
									value={helperInputInfo.scientificTeachers}
									values={
										scientificPoolNames.length
											? scientificPoolNames
											: showAllTeachers
												? allTeachersNames
												: districtTeachersNames
									}
									valueChangeHandler={scientificTeachersHandler}
									helperText={`Możesz wskazać do ${maxScientificGuardians} opiekunów naukowych (lub szkolno-naukowych).`}
								/>
								<FormControlLabel
									control={
										<Checkbox
											sx={{
												borderRadius: 3,
												width: 16,
												height: 16,
												mr: '6px',
												mt: '2px',
											}}
										/>
									}
									label='Pokaż opiekunów naukowych spoza mojego okręgu'
									onChange={(e: any) => setShowAllTeachers(e.target.checked)}
									sx={{
										ml: '2px',
										alignItems: 'flex-start',
										'& .MuiFormControlLabel-label': {
											lineHeight: 1.35,
										},
									}}
								/>
								{maxSchoolGuardians > 0 && (
									<>
										<FormComboBox
											id={'school-teacher'}
											label={'Opiekun szkolny (jeśli już znasz)'}
											multiple={false}
											validated={validated}
											errorMsg={''}
											value={helperInputInfo.schoolTeacher}
											values={schoolPoolNames.length ? schoolPoolNames : districtTeachersNames}
											valueChangeHandler={schoolTeacherHandler}
										/>
										<Typography
											variant='body2'
											color='text.secondary'
											sx={{ mt: '-4px' }}
										>
											Jeśli nie wskażesz, sekretarz okręgu przydzieli opiekuna po ustaleniu szkoły. Może to być także
											osoba zadeklarowana jako opiekun szkolno-naukowy.
										</Typography>
									</>
								)}
							</>
						) : (
							<>
								<FormComboBox
									id={'teacher'}
									label={'Nauczyciele'}
									multiple={true}
									multipleMax={maximumNumberOfTeachers}
									validated={validated}
									errorMsg={formValidation.teacherError}
									value={helperInputInfo.teachers}
									values={showAllTeachers ? allTeachersNames : districtTeachersNames}
									valueChangeHandler={teacherHandler}
									helperText={`Możesz wskazać do ${maximumNumberOfTeachers} nauczycieli (kolejne wybory z listy).`}
								/>
								<FormControlLabel
									control={
										<Checkbox
											sx={{
												borderRadius: 3,
												width: 16,
												height: 16,
												mr: '6px',
											}}
										/>
									}
									label='Pokaż nauczycieli spoza mojego okręgu'
									onChange={(e: any) => setShowAllTeachers(e.target.checked)}
									sx={{
										ml: '2px',
									}}
								/>
							</>
						)}
					</Box>
					<TextWithDivider
						text={'Dodatkowe'}
						dividerColor={palette.secondary.light}
						sx={{
							mt: '32px',
						}}
					/>
					<Box
						sx={{
							'& span': {
								fontSize: typography.subtitle2.fontSize,
								fontWeight: '500',
								color: palette.primary.main,
							},
						}}
					>
						<FormControlLabel
							control={
								<Checkbox
									checked={formInfo.specialCertificate}
									sx={{
										borderRadius: 3,
										width: 16,
										height: 16,
										mr: '6px',
									}}
								/>
							}
							label='Posiadam orzeczenie lub opinie o dysgrafii'
							onChange={specialCertificateHandler}
							sx={{
								ml: '2px',
							}}
						/>
						{formInfo.specialCertificate ? (
							<Box sx={{ ml: '2px', display: 'flex', flexDirection: 'column', gap: 1 }}>
								<Typography
									variant='body2'
									sx={{ color: 'text.secondary' }}
								>
									Możesz od razu załączyć skan(y) opinii (PDF/JPG/PNG, maks. 10 MB każdy) — np. dwie strony jako dwa
									zdjęcia. Jeśli nie masz plików pod ręką — wgrasz je później na stronie Konta.
								</Typography>
								<input
									type='file'
									multiple
									accept='.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png'
									onChange={(e) => setOpinionFiles(e.target.files ? Array.from(e.target.files) : [])}
								/>
								{opinionFiles.length > 0 && (
									<Typography variant='body2'>
										Wybrane ({opinionFiles.length}): {opinionFiles.map((f) => f.name).join(', ')}
									</Typography>
								)}
							</Box>
						) : (
							<Typography
								variant='body2'
								sx={{ ml: '2px', color: 'text.secondary' }}
							>
								Po zaznaczeniu orzeczenia możesz załączyć skan(y) w tym formularzu albo później na Koncie.
							</Typography>
						)}
					</Box>
					<TextWithDivider
						text={'Zgody musi wypełnić osoba pełnoletnia'}
						dividerColor={palette.secondary.light}
						sx={{
							mt: '32px',
						}}
					/>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
							'& span': {
								fontSize: typography.subtitle2.fontSize,
								fontWeight: '500',
								color: palette.primary.main,
							},
						}}
					>
						<FormControlLabel
							control={
								<Checkbox
									sx={{
										borderRadius: 3,
										width: 16,
										height: 16,
										mr: '6px',
									}}
								/>
							}
							label='Zgoda RODO'
							onChange={rodoHandler}
							sx={{
								ml: '2px',
							}}
						/>
						<FormControlLabel
							control={
								<Checkbox
									sx={{
										borderRadius: 3,
										width: 16,
										height: 16,
										mr: '6px',
									}}
								/>
							}
							label='Oświadczam, że powyższe dane są zgodne ze stanem faktycznym'
							onChange={consentHandler}
							sx={{
								ml: '2px',
							}}
						/>
					</Box>
				</Box>
				<Box
					mt={'16px'}
					mb={'56px'}
					alignSelf={'center'}
					display={'flex'}
					justifyContent={'center'}
					alignItems={'center'}
					position={'relative'}
				>
					<Box alignSelf={'center'}>
						<MainButton
							title={'Zarejestruj się'}
							darkVersion
							onClick={actionSend}
							sx={{
								alignSelf: 'center',
								lineHeight: '20px',
								letterSpacing: '0.18px',
							}}
						/>
					</Box>
					<CircularProgress
						sx={{
							display: loading ? 'block' : 'none',
							position: 'absolute',
							right: '-60px',
						}}
					/>
				</Box>
			</Grid>
			<Box mt={'64px'}>
				<DividerWithButton
					label={'Masz już konto?'}
					hrefPath={loginPage}
					buttonTitle={'Zaloguj się'}
				/>
			</Box>
			<Dialog
				open={priorPasswordDialogOpen}
				onClose={() => !priorVerifyLoading && setPriorPasswordDialogOpen(false)}
				fullWidth
				maxWidth='sm'
			>
				<DialogTitle>Konto z poprzedniej edycji</DialogTitle>
				<DialogContent>
					<Typography
						variant='body2'
						sx={{ mb: 2 }}
					>
						Podaj hasło do konta powiązanego z tym adresem e-mail, aby wczytać dane na bieżącą edycję.
					</Typography>
					{priorResetSent && (
						<Typography
							variant='body2'
							color='text.secondary'
							sx={{ mb: 2 }}
						>
							Sprawdź skrzynkę e-mail, ustaw nowe hasło z linku, wróć tutaj i wpisz je poniżej, aby kontynuować.
						</Typography>
					)}
					<FormTextField
						id='prior-password'
						type='password'
						label='Hasło do istniejącego konta'
						validated={validated}
						errorMsg=''
						value={priorVerifyPassword}
						valueChangeHandler={setPriorVerifyPassword}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
					<Link
						component='button'
						type='button'
						variant='body2'
						onClick={requestPriorPasswordReset}
						sx={{ mr: 'auto' }}
						disabled={priorResetLoading || priorVerifyLoading}
					>
						{priorResetLoading ? 'Wysyłanie…' : 'Nie pamiętam hasła'}
					</Link>
					<MainButton
						title='Anuluj'
						onClick={() => setPriorPasswordDialogOpen(false)}
						sx={{ width: 'auto', px: 3 }}
					/>
					<MainButton
						title={priorVerifyLoading ? 'Sprawdzanie…' : 'Potwierdź hasło'}
						darkVersion
						onClick={confirmPriorPassword}
						disabled={priorVerifyLoading}
						sx={{ width: 'auto', px: 3 }}
					/>
				</DialogActions>
			</Dialog>
			<Dialog
				open={summaryOpen}
				onClose={() => {
					if (!loading) {
						setSummaryOpen(false);
					}
				}}
				fullWidth
				maxWidth='md'
			>
				<DialogTitle sx={{ pb: 1 }}>
					<Box
						display='flex'
						alignItems='center'
						gap={1}
					>
						<CheckCircleOutlineIcon sx={{ color: palette.primary.main }} />
						<Typography
							variant='h5'
							color={palette.primary.main}
						>
							Sprawdź dane przed wysłaniem
						</Typography>
					</Box>
				</DialogTitle>
				<DialogContent sx={{ pt: 1 }}>
					<Box
						sx={{
							mb: 2,
							px: 1.5,
							py: 1,
							borderRadius: 2,
							backgroundColor: palette.primary.light,
							border: `1px solid ${palette.primary.main}`,
						}}
					>
						<Typography
							variant='body2'
							sx={{ color: palette.primary.main }}
						>
							Prosimy o potwierdzenie poprawności danych. W przypadku konieczności wprowadzenia zmian prosimy wybrać{' '}
							<b>Wróć do edycji</b>.
						</Typography>
					</Box>
					<Box
						display='flex'
						flexDirection='column'
						gap={2}
					>
						{summarySections.map((section, sectionIdx) => (
							<Box
								key={section.title}
								sx={{
									border: `1px solid ${palette.primary.light}`,
									borderRadius: 3,
									p: { xs: 1.5, md: 2 },
									boxShadow: '0 3px 10px rgba(0,0,0,0.06)',
									backgroundColor: '#fff',
								}}
							>
								<Box
									display='flex'
									alignItems='center'
									justifyContent='space-between'
									mb={1}
								>
									<Box
										display='flex'
										alignItems='center'
										gap={1}
									>
										<Box sx={{ color: palette.primary.main, display: 'flex', alignItems: 'center' }}>
											{sectionIconByTitle[section.title]}
										</Box>
										<Typography
											variant='h6'
											sx={{ color: palette.primary.main, fontSize: { xs: '1rem', md: '1.1rem' } }}
										>
											{section.title}
										</Typography>
									</Box>
									<Chip
										size='small'
										label={`${section.rows.length} ${getPoleWord(section.rows.length)}`}
										sx={{ color: palette.primary.main, borderColor: palette.primary.main }}
										variant='outlined'
									/>
								</Box>
								{section.rows.map((item) => (
									<Grid
										key={`${section.title}-${item.label}`}
										container
										alignItems='center'
										sx={{
											display: 'grid',
											gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 32%) 1fr' },
											py: 0.75,
											columnGap: 2,
											rowGap: 0.8,
											borderBottom: `1px dashed ${palette.primary.light}`,
											'&:last-of-type': { borderBottom: 'none' },
										}}
									>
										<Typography
											variant='body1'
											sx={{ color: palette.primary.main, fontWeight: 700, textAlign: 'left', minWidth: 'fit-content' }}
										>
											{item.label}
										</Typography>
										<Box
											sx={{
												flex: 1,
												pl: { xs: 0, md: 2 },
												ml: { xs: 0, md: 'max(12%, 100px)' },
												py: 0.4,
												px: 1.2,
												borderRadius: 1.5,
												backgroundColor: 'rgba(0, 83, 160, 0.06)',
											}}
										>
											<Typography
												variant='body1'
												sx={{ color: palette.primary.main, textAlign: 'left', wordBreak: 'break-word' }}
											>
												{item.value}
											</Typography>
										</Box>
									</Grid>
								))}
								{sectionIdx < summarySections.length - 1 && <Divider sx={{ mt: 1 }} />}
							</Box>
						))}
					</Box>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'space-between' }}>
					<MainButton
						title='Wróć do edycji'
						onClick={() => setSummaryOpen(false)}
						sx={{ width: 'auto', px: 3 }}
					/>
					<MainButton
						title={loading ? 'Wysyłanie...' : 'Potwierdzam i wysyłam'}
						darkVersion
						onClick={submitRegistration}
						sx={{ width: 'auto', px: 3 }}
					/>
				</DialogActions>
			</Dialog>
		</Grid>
	);
};

export default RegisterStudent;
